import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { AnimatePresence, motion } from 'framer-motion';

import { useAuth } from '../../contexts/AuthContext';
import Button from '../Button';
import { ReactComponent as AccountIcon } from '../../assets/icons/account-icon.svg';
import { ReactComponent as PaymentIcon } from '../../assets/icons/payment-icon.svg';
import { ReactComponent as SettingsIcon } from '../../assets/icons/settings-icon.svg';
import { ReactComponent as LogoutIcon } from '../../assets/icons/logout-icon.svg';
import { ReactComponent as BellNotificationIcon } from '../../assets/icons/bell-notification-icon.svg';
import { ReactComponent as QuestionsIcon } from '../../assets/icons/questions-icon.svg';

import { AccountPanelProps } from './AccountPanel.types';
import styles from './AccountPanel.module.scss';

export function AccountPanel({ userName, userInitials }: AccountPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(prev => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleMenuButtonClick = (onClick: () => void) => {
    onClick();
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const menuItems = [
    {
      label: 'Личный профиль',
      icon: <AccountIcon className={styles.menuItems__itemIcon} />,
      onClick: () => {
        toast.info('Раздел в разработке');
      },
    },
    {
      label: 'Настройки',
      icon: <SettingsIcon className={styles.menuItems__itemIcon} />,
      onClick: () => {
        toast.info('Раздел в разработке');
      },
    },
    {
      label: 'Платежи',
      icon: <PaymentIcon className={styles.menuItems__itemIcon} />,
      onClick: () => {
        toast.info('Раздел в разработке');
      },
    },
    {
      label: 'Поддержка',
      icon: <QuestionsIcon className={styles.menuItems__itemIcon} />,
      onClick: () => {
        toast.info('Раздел в разработке');
      },
    },
  ];

  return (
    <div className={styles.panel} ref={menuRef}>
      <QuestionsIcon className={styles.panelIcon} />
      <BellNotificationIcon className={styles.panelIcon} />
      <Button
        variant="text"
        className={styles.dropdown}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggleMenu}
      >
        <div className={styles.dropdown__verticalLine}></div>
        <div className={styles.dropdown__frame}>
          <div className={styles.dropdown__initials}>{userInitials}</div>
        </div>
        <FiChevronDown
          className={classNames(styles.dropdown__chevronDown, {
            [styles.open]: isOpen,
          })}
        />

        {/* @ts-expect-error framer-motion types incompatible with @types/react 18 */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              id="dropdown"
              className={styles.menu}
              role="menu"
              tabIndex={0}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  setIsOpen(false);
                }
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className={styles.menuHeader}>
                <div className={styles.menuHeader__avatar}>
                  <span>{userInitials}</span>
                </div>
                <div className={styles.userInfo}>
                  <div className={styles.dropdown__username}>{userName}</div>
                </div>
              </div>
              <hr className={styles.menuDivider} />
              <ul className={styles.menuItems}>
                {menuItems.map((item, idx) => (
                  <li
                    key={idx}
                    className={styles.menuItems__item}
                    tabIndex={0}
                    role="menuitem"
                    onClick={() => handleMenuButtonClick(item.onClick)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleMenuButtonClick(item.onClick);
                      }
                    }}
                  >
                    {item.icon}
                    <div className={styles.menuItems__itemText}>{item.label}</div>
                  </li>
                ))}
              </ul>
              <hr className={styles.menuDivider} />
              <ul className={styles.menuItems}>
                <li
                  className={styles.menuItems__item}
                  tabIndex={0}
                  role="menuitem"
                  onClick={handleLogout}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleLogout();
                    }
                  }}
                >
                  <LogoutIcon className={styles.menuItems__itemIcon} />
                  <div className={styles.menuItems__itemText}>Выйти</div>
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </Button>
    </div>
  );
}
